import React, { useEffect, useRef } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import CookieConsentBanner from './components/CookieConsentBanner';
import AdBlockDetector from './components/AdBlockDetector';
import { Footer } from './components/Footer';
import { liveAudienceApi } from './lib/api';

// Pages
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import FilmDetailPage from './pages/FilmDetailPage';
import WatchPage from './pages/WatchPage';
import MyListPage from './pages/MyListPage';
import SearchPage from './pages/SearchPage';
import ProfilePage from './pages/ProfilePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import AdminPage from './pages/AdminPage';
import PricingPage from './pages/PricingPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import CreatorRegisterPage from './pages/CreatorRegisterPage';
import CompanyDashboardPage from './pages/CompanyDashboardPage';
import ContentPerformancePage from './pages/ContentPerformancePage';
import UploadContentPage from './pages/UploadContentPage';
import OwnerDashboardPage from './pages/OwnerDashboardPage';
import CreatorStorePage from './pages/CreatorStorePage';
import ShopPurchaseSuccessPage from './pages/ShopPurchaseSuccessPage';
import CheckoutPage from './pages/CheckoutPage';
import TrackingPage from './pages/TrackingPage';
import MyOrdersPage from './pages/MyOrdersPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import LegalPage from './pages/LegalPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';

// Components
import { AuthCallback } from './components/AuthCallback';
import PolicyReconfirmation from './components/PolicyReconfirmation';

// Protected Route wrapper — includes policy reconfirmation check
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <PolicyReconfirmation>{children}</PolicyReconfirmation>;
};

// Owner Route wrapper
const OwnerRoute = ({ children }) => {
  const { isAuthenticated, isOwnerOrAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isOwnerOrAdmin) {
    return <Navigate to="/browse" replace />;
  }

  return children;
};

// Router component that handles auth callback detection
const GlobalPresence = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    const sendPresence = () => {
      liveAudienceApi.presence(location.pathname).catch(() => {});
    };

    sendPresence();
    intervalRef.current = setInterval(sendPresence, 30000);

    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isAuthenticated, location.pathname]);

  return null;
};

// Router component that handles auth callback detection
const AppRouter = () => {
  const location = useLocation();

  // Only intercept session_id for Google OAuth callbacks.
  // Exclude paths that use session_id for other purposes (e.g. Stripe redirects)
  const isStripeReturn = location.pathname === '/payment/success'
    || location.pathname.startsWith('/company/dashboard');

  if (!isStripeReturn) {
    // Check for session_id in URL fragment (Google OAuth callback)
    if (location.hash?.includes('session_id=')) {
      return <AuthCallback />;
    }

    // Also check URL search params as fallback for some mobile browsers
    if (location.search?.includes('session_id=')) {
      return <AuthCallback />;
    }
  }

  return (
    <Routes>
      {/* Auth callback route - explicit route for mobile compatibility */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/register" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/browse" element={<HomePage />} />
      <Route path="/browse/films" element={<HomePage />} />
      <Route path="/browse/series" element={<HomePage />} />
      <Route path="/film/:filmId" element={<FilmDetailPage />} />
      <Route path="/watch/:filmId" element={<WatchPage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/pricing" element={<PricingPage />} />

      {/* Creator registration - public but requires auth for step 2 */}
      <Route path="/creator/register" element={<CreatorRegisterPage />} />
      <Route path="/company/register" element={<CreatorRegisterPage />} />

      {/* Creator Store - public */}
      <Route path="/shop/creator/:companyId" element={<CreatorStorePage />} />
      <Route path="/shop/purchase/success" element={<ShopPurchaseSuccessPage />} />

      {/* Tracking - public */}
      <Route path="/track" element={<TrackingPage />} />

      {/* Legal pages - public */}
      <Route path="/legal/:slug" element={<LegalPage />} />

      {/* Protected routes */}
      <Route
        path="/my-list"
        element={
          <ProtectedRoute>
            <MyListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute>
            <SubscriptionSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkout"
        element={
          <ProtectedRoute>
            <CheckoutPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-orders"
        element={
          <ProtectedRoute>
            <MyOrdersPage />
          </ProtectedRoute>
        }
      />

      {/* Company dashboard */}
      <Route
        path="/company/dashboard"
        element={
          <ProtectedRoute>
            <CompanyDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/company/upload"
        element={
          <ProtectedRoute>
            <UploadContentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/company/content/:filmId/performance"
        element={
          <ProtectedRoute>
            <ContentPerformancePage />
          </ProtectedRoute>
        }
      />

      {/* Payment routes */}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />

      {/* Owner routes */}
      <Route
        path="/owner"
        element={
          <OwnerRoute>
            <OwnerDashboardPage />
          </OwnerRoute>
        }
      />

      {/* Legacy admin route - redirects to owner dashboard */}
      <Route
        path="/admin"
        element={
          <OwnerRoute>
            <AdminPage />
          </OwnerRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Footer visibility — hide on watch page
const FooterWrapper = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/watch/')) return null;
  return <Footer />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <GlobalPresence />
          <AppRouter />
          <FooterWrapper />
          <Toaster 
            position="bottom-right"
            toastOptions={{
              style: {
                background: '#0F0B1E',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                color: '#fff',
              },
            }}
          />
          <CookieConsentBanner />
          <AdBlockDetector />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
